//Angular
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

//Ionic
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { Storage, IonicStorageModule } from '@ionic/storage-angular';

//External
import { JwtModule, JWT_OPTIONS } from '@auth0/angular-jwt';

import { NgxGpAutocompleteModule } from '@angular-magic/ngx-gp-autocomplete';
import { Loader } from '@googlemaps/js-api-loader';

//Translate
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

//Internal
import { AuthFeatureModule } from './features/auth/auth.module';
import { Services } from './services/services';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { PipesModule } from './pipes/pipes.module';
import { AppConfig } from './variables';
import { version as appVersion } from './version';
import { DefaultsService } from './services/defaults/defaults.service';
import { SentryIonicErrorHandler } from './error-handler';
import { LanguageService } from './services/language/language.service';
import { DirectivesModule } from './directives/directives.module';
import { LoginAlertModule } from './components/login-alert/login-alert.module';
import { APP_CONFIG, APP_TYPE, APP_VERSION } from '@smartcharge/shared/core/config';
import { STORAGE } from '@smartcharge/shared/core/storage';
import { EnvironmentService } from '@smartcharge/shared/core/environment';
import { SharedModule } from '@smartcharge/shared/shared.module';
import { MigrationComponent } from './components/migration/migration.component';
import { AddPaymentModule } from './components/add-payment/add-payment.module';

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    IonicModule.forRoot({
      innerHTMLTemplatesEnabled: true,
      useSetInputAPI: true
    }),
    IonicStorageModule.forRoot(),
    TranslateModule.forRoot({
      loader: provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json?v=' + appVersion })
    }),
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: jwtOptionsFactory,
        deps: [Storage]
      }
    }),
    NgxGpAutocompleteModule,
    AppRoutingModule,
    PipesModule,
    AuthFeatureModule,
    DirectivesModule,
    SharedModule,
    MigrationComponent,
    AddPaymentModule
  ],
  providers: [
    //OneSignal,
    Storage,
    TranslateService,
    LoginAlertModule,
    //end Native
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      provide: APP_CONFIG,
      useValue: new AppConfig()
    },
    {
      provide: APP_TYPE,
      useValue: 'app'
    },
    {
      provide: APP_VERSION,
      useValue: appVersion
    },
    {
      provide: STORAGE,
      useExisting: Storage
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService, Storage, DefaultsService, LanguageService, APP_CONFIG, EnvironmentService],
      multi: true
    },
    {
      provide: Loader,
      useValue: new Loader({
        apiKey: 'AIzaSyBNAJJ0c9-VScOE6H78bO-pWqKBAwqZg7E',
        libraries: ['places']
      })
    },
    { provide: ErrorHandler, useClass: SentryIonicErrorHandler },
    //Services
    Services,
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {}

export function appInitializerFactory(
  translate: TranslateService,
  storage: Storage,
  defaults: DefaultsService,
  languageService: LanguageService,
  config: AppConfig,
  environmentService: EnvironmentService
) {
  return async () => {
    await storage.create();

    await defaults.init();
    await environmentService.init();

    translate.addLangs(config.availableLangs);
    translate.setFallbackLang(config.defaultLang);

    const lang = await languageService.getLanguage();
    translate.use(lang);
  };
}

export function jwtOptionsFactory(storage: Storage) {
  return {
    tokenGetter: async () => {
      return await storage.get('token');
    },
    allowedDomains: [
      'api.current.eco',
      'api.smartcharge.io',
      'app.smartcharge.io',
      'smartchargeapipd.azurewebsites.net',
      'meshcrafts-demo-api-app-service-stagging.azurewebsites.net',
      'meshcrafts-demo-api-app-service-qa.azurewebsites.net'
    ]
  };
}
